import type { GeofenceSetup } from "../domain/geofenceReminder";
import { roundedPoint } from "../domain/location";
import type { GeoPoint } from "../domain/location";
import { ndefUri, uidHex } from "../domain/nfc";
import type { NdefRecordData } from "../domain/nfc";
import { isInternalPath } from "../sw/notifications";
import { nfcReadTimeoutMs, positionOverallLimitMs } from "./PlatformServices";
import type {
  GeofencePermission,
  GeofenceReminders,
  GeofenceStatus,
  NfcReadResult,
  NfcReading,
  PlatformServices,
} from "./PlatformServices";

/** Handle of a Capacitor plugin listener. */
export interface ListenerHandle {
  remove(): Promise<void>;
}

/** The part of the Capacitor `App` plugin the platform uses (injected for tests). */
export interface AppPluginPort {
  getLaunchUrl(): Promise<{ readonly url: string } | undefined>;
  addListener(
    eventName: "appStateChange",
    listener: (state: { readonly isActive: boolean }) => void,
  ): Promise<ListenerHandle>;
  addListener(
    eventName: "appUrlOpen",
    listener: (event: { readonly url: string }) => void,
  ): Promise<ListenerHandle>;
}

/** The part of the Capacitor `Network` plugin the platform uses (injected for tests). */
export interface NetworkPluginPort {
  getStatus(): Promise<{ readonly connected: boolean }>;
  addListener(
    eventName: "networkStatusChange",
    listener: (status: { readonly connected: boolean }) => void,
  ): Promise<ListenerHandle>;
}

/** The part of the Capacitor `PushNotifications` plugin the platform uses (injected for tests). */
export interface NotificationPluginPort {
  addListener(
    eventName: "pushNotificationActionPerformed",
    listener: (action: { readonly notification: { readonly data?: unknown } }) => void,
  ): Promise<ListenerHandle>;
  removeAllDeliveredNotifications(): Promise<void>;
}

/** The app's own `AppBadge` plugin (iOS only, `ios/App/App/AppBadgePlugin.swift`). */
export interface AppBadgePluginPort {
  set(options: { readonly count: number }): Promise<void>;
}

/** State of a location permission of the Capacitor `Geolocation` plugin. */
export type LocationPermissionState = "prompt" | "prompt-with-rationale" | "granted" | "denied";

/** Location permissions of the Capacitor `Geolocation` plugin. */
export interface LocationPermissions {
  readonly location: LocationPermissionState;
  readonly coarseLocation: LocationPermissionState;
}

/**
 * The part of the Capacitor `Geolocation` plugin the platform uses (T-175, ADR-0145; injected for
 * tests). Only the coarse permission „while using the app“ is requested, never „always“.
 */
export interface GeolocationPluginPort {
  checkPermissions(): Promise<LocationPermissions>;
  requestPermissions(options: {
    readonly permissions: "coarseLocation"[];
  }): Promise<LocationPermissions>;
  getCurrentPosition(options: {
    readonly enableHighAccuracy: boolean;
    readonly timeout: number;
    readonly maximumAge: number;
  }): Promise<{ readonly coords: { readonly latitude: number; readonly longitude: number } }>;
}

/** A discovery of the NFC plugin: the tag with its UID and NDEF message. */
export interface NfcPluginEvent {
  readonly type: string;
  readonly tag: {
    readonly id?: readonly number[];
    readonly ndefMessage?: readonly NdefRecordData[] | null;
  };
}

/**
 * The part of the `@capgo/capacitor-nfc` plugin the platform uses (T-174, ADR-0147; injected for tests). Tags are read
 * only in the foreground: Android in reader mode while a reading or watch runs, iOS in a reader session with the system
 * sheet (`NFCTagReaderSession`).
 */
export interface NfcPluginPort {
  isSupported(): Promise<{ readonly supported: boolean }>;
  getStatus(): Promise<{ readonly status: string }>;
  startScanning(options: {
    readonly invalidateAfterFirstRead?: boolean;
    readonly alertMessage?: string;
    readonly iosSessionType?: "ndef" | "tag";
  }): Promise<void>;
  stopScanning(): Promise<void>;
  addListener(
    eventName: "nfcEvent",
    listener: (event: NfcPluginEvent) => void,
  ): Promise<ListenerHandle>;
  addListener(eventName: "nfcSessionEnd", listener: () => void): Promise<ListenerHandle>;
}

/**
 * The app's own `GeofenceReminder` plugin (T-188, ADR-0167; `ios/App/App/GeofenceReminderPlugin.swift`,
 * `android/app/src/main/java/de/zeitavo/app/GeofenceReminderPlugin.java`; injected for tests). The
 * system watches the regions and shows local notifications; the plugin never reads a position.
 */
export interface GeofenceReminderPluginPort {
  checkStatus(): Promise<unknown>;
  askPermissions(): Promise<unknown>;
  configure(setup: GeofenceSetup): Promise<void>;
  clear(): Promise<void>;
  openSettings(): Promise<void>;
  addListener(
    eventName: "reminderOpened",
    listener: (event: { readonly kind?: unknown }) => void,
  ): Promise<ListenerHandle>;
}

/** Dependencies of {@link NativePlatformServices}. */
export interface NativePlatformPorts {
  /** The native platform. */
  readonly name: "ios" | "android";
  /** Capacitor `App` plugin. */
  readonly app: AppPluginPort;
  /** Capacitor `Network` plugin. */
  readonly network: NetworkPluginPort;
  /** Capacitor `PushNotifications` plugin. */
  readonly notifications: NotificationPluginPort;
  /**
   * Icon badge of iOS. Android has no badge count API; launchers show a dot for the delivered
   * reminder notifications instead.
   */
  readonly badge: AppBadgePluginPort | null;
  /** Capacitor `Geolocation` plugin for the location when clocking (T-175). */
  readonly geolocation: GeolocationPluginPort;
  /** NFC plugin for clocking at tags (T-174); without it the app has no NFC functions. */
  readonly nfc?: NfcPluginPort;
  /** The app's own plugin of the geofence reminder (T-188); without it the function is hidden. */
  readonly geofence?: GeofenceReminderPluginPort;
  /** Timer of the overall limit of a position request; the global timer by default. */
  readonly setTimeout?: (callback: () => void, delayMs: number) => unknown;
}

/**
 * Reads the application path of a tapped notification (`url` of the message data, T-081).
 *
 * @param data - Data of the notification.
 * @returns The internal path, or `/` for missing or foreign targets.
 */
export function notificationPath(data: unknown): string {
  const url =
    typeof data === "object" && data !== null && "url" in data && typeof data.url === "string"
      ? data.url
      : "/";
  const path = isInternalPath(url) ? url : "/";

  return path;
}

/**
 * Subscribes through an asynchronous plugin listener and returns a synchronous unsubscribe
 * function. A subscription ended before the plugin answered is removed as soon as it exists.
 *
 * @param add - Registers the listener at the plugin.
 * @returns A function that ends the subscription.
 */
export function subscribePlugin(add: () => Promise<ListenerHandle>): () => void {
  let handle: ListenerHandle | null = null;
  let ended = false;
  add().then(
    (registered) => {
      if (ended) {
        void registered.remove();
      } else {
        handle = registered;
      }
    },
    () => undefined,
  );
  const unsubscribe = (): void => {
    ended = true;
    void handle?.remove();
    handle = null;
  };

  return unsubscribe;
}

/**
 * {@link PlatformServices} of the Capacitor apps (T-080, T-082): lifecycle through
 * `appStateChange`, connectivity through the `Network` plugin, app URLs through `appUrlOpen` and
 * the launch URL, icon badge through the app's `AppBadge` plugin (iOS).
 */
export class NativePlatformServices implements PlatformServices {
  /** @inheritdoc */
  readonly name: "ios" | "android";

  /** @inheritdoc */
  readonly isNative = true;

  /** @inheritdoc */
  readonly watchNfcTags?: (listener: (reading: NfcReading) => void) => () => void;

  /** @inheritdoc */
  readonly geofence?: GeofenceReminders;

  private readonly ports: NativePlatformPorts;

  private foreground = true;

  private launchUrlDelivered = false;

  private cancelReading: (() => void) | null = null;

  /**
   * Creates the services and starts following the foreground state.
   *
   * @param ports - Capacitor plugins.
   */
  constructor(ports: NativePlatformPorts) {
    this.ports = ports;
    this.name = ports.name;

    // Personal chips at a terminal are read on Android only (variant B, T-174); iOS reads only on an action.
    const nfc = ports.nfc;

    if (ports.name === "android" && nfc !== undefined) {
      this.watchNfcTags = (listener): (() => void) => watchTags(nfc, listener);
    }

    if (ports.geofence !== undefined) {
      this.geofence = geofenceReminders(ports.geofence, ports.name);
    }

    subscribePlugin(() =>
      ports.app.addListener("appStateChange", (state) => {
        this.foreground = state.isActive;
      }),
    );
  }

  /** @inheritdoc */
  async isOnline(): Promise<boolean> {
    const status = await this.ports.network.getStatus();
    const online = status.connected;

    return online;
  }

  /** @inheritdoc */
  onNetworkChange(listener: (online: boolean) => void): () => void {
    const unsubscribe = subscribePlugin(() =>
      this.ports.network.addListener("networkStatusChange", (status) => {
        listener(status.connected);
      }),
    );

    return unsubscribe;
  }

  /** @inheritdoc */
  onResume(listener: () => void): () => void {
    const unsubscribe = subscribePlugin(() =>
      this.ports.app.addListener("appStateChange", (state) => {
        if (state.isActive) {
          listener();
        }
      }),
    );

    return unsubscribe;
  }

  /** @inheritdoc */
  isInForeground(): boolean {
    const foreground = this.foreground;

    return foreground;
  }

  /** @inheritdoc */
  onAppUrlOpen(listener: (url: string) => void): () => void {
    let active = true;
    let lastUrl: string | null = null;
    // iOS retains the URL of a cold start as event as well: consecutive duplicates are dropped.
    const deliver = (url: string): void => {
      if (active && url !== "" && url !== lastUrl) {
        lastUrl = url;
        listener(url);
      }
    };
    const unsubscribeEvents = subscribePlugin(() =>
      this.ports.app.addListener("appUrlOpen", (event) => {
        deliver(event.url);
      }),
    );

    // A URL that started the app (cold start from the login callback) is delivered only once.
    if (!this.launchUrlDelivered) {
      this.launchUrlDelivered = true;
      this.ports.app.getLaunchUrl().then(
        (launch) => {
          if (launch !== undefined) {
            deliver(launch.url);
          }
        },
        () => undefined,
      );
    }

    const unsubscribe = (): void => {
      active = false;
      unsubscribeEvents();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  onNotificationOpened(listener: (path: string) => void): () => void {
    const geofence = this.ports.geofence;
    const unsubscribePush = subscribePlugin(() =>
      this.ports.notifications.addListener("pushNotificationActionPerformed", (action) => {
        listener(notificationPath(action.notification.data));
      }),
    );
    // A tapped geofence reminder opens the clock screen; clocking stays an action of the person (T-188).
    const unsubscribeGeofence =
      geofence === undefined
        ? (): void => undefined
        : subscribePlugin(() =>
            geofence.addListener("reminderOpened", () => {
              listener("/");
            }),
          );
    const unsubscribe = (): void => {
      unsubscribePush();
      unsubscribeGeofence();
    };

    return unsubscribe;
  }

  /** @inheritdoc */
  async setBadge(count: number): Promise<void> {
    const shown = Math.max(0, Math.floor(count));

    try {
      await this.ports.badge?.set({ count: shown });
    } catch {
      // The badge is a convenience; the open days are shown in the application.
    }
  }

  /** @inheritdoc */
  async clearDeliveredReminders(): Promise<void> {
    try {
      // Reminders about days that are closed now are outdated (also removes the Android dot).
      await this.ports.notifications.removeAllDeliveredNotifications();
    } catch {
      // The reminders stay in the notification centre; the open days are shown in the application.
    }
  }

  /** @inheritdoc */
  async currentPosition(timeoutMs: number): Promise<GeoPoint | null> {
    const schedule =
      this.ports.setTimeout ?? ((callback, delayMs): unknown => setTimeout(callback, delayMs));
    // One overall limit, the permission dialog included, so clocking never waits long (T-175).
    const limit = new Promise<null>((resolve) => {
      schedule(() => {
        resolve(null);
      }, positionOverallLimitMs);
    });
    const position = await Promise.race([this.askPosition(timeoutMs), limit]);

    return position;
  }

  /** @inheritdoc */
  async nfcSupported(): Promise<boolean> {
    try {
      const result = (await this.ports.nfc?.isSupported()) ?? { supported: false };
      const supported = result.supported;

      return supported;
    } catch {
      return false;
    }
  }

  /** @inheritdoc */
  async readNfcTag(prompt: string): Promise<NfcReadResult> {
    const cancelled: NfcReadResult = { kind: "cancelled" };
    const nfc = this.ports.nfc;

    if (nfc === undefined) {
      return cancelled;
    }

    if (await isSwitchedOff(nfc)) {
      const disabled: NfcReadResult = { kind: "disabled" };
      return disabled;
    }

    const schedule =
      this.ports.setTimeout ?? ((callback, delayMs): unknown => setTimeout(callback, delayMs));
    let resolveReading: (result: NfcReadResult) => void = () => undefined;
    const outcome = new Promise<NfcReadResult>((resolve) => {
      resolveReading = resolve;
    });
    const handles: ListenerHandle[] = [];
    // iOS keeps the end of an earlier session for the next listener (retained event): an end delivered before this
    // session started belongs to the earlier one and is ignored (F7 of the review of T-174).
    let started = false;
    this.cancelReading = (): void => {
      resolveReading(cancelled);
    };

    try {
      handles.push(
        await nfc.addListener("nfcEvent", (event) => {
          resolveReading({ kind: "read", reading: readingOf(event) });
        }),
      );
      // The person closed the sheet or it timed out (iOS).
      handles.push(
        await nfc.addListener("nfcSessionEnd", () => {
          if (started) {
            resolveReading(cancelled);
          }
        }),
      );
      schedule(() => {
        resolveReading(cancelled);
      }, nfcReadTimeoutMs);
      await nfc.startScanning({
        invalidateAfterFirstRead: true,
        alertMessage: prompt,
        iosSessionType: "tag",
      });
      started = true;
    } catch {
      // No reader or no permission: nothing is read.
      resolveReading(cancelled);
    }

    const result = await outcome;
    this.cancelReading = null;
    await Promise.allSettled([nfc.stopScanning(), ...handles.map((handle) => handle.remove())]);

    return result;
  }

  /** @inheritdoc */
  cancelNfcRead(): void {
    this.cancelReading?.();
  }

  /**
   * Asks for the coarse permission once and for one position.
   *
   * @param timeoutMs - Time limit of the position request.
   * @returns The rounded position, or `null`.
   */
  private async askPosition(timeoutMs: number): Promise<GeoPoint | null> {
    try {
      const geolocation = this.ports.geolocation;
      const status = await geolocation.checkPermissions();
      const decided = isGranted(status) || status.coarseLocation === "denied";
      // The system dialog asks once; Android gets the coarse permission only (spec 12.4).
      const permissions = decided
        ? status
        : await geolocation.requestPermissions({ permissions: ["coarseLocation"] });

      if (!isGranted(permissions)) {
        return null;
      }

      const position = await geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: timeoutMs,
        maximumAge: 0,
      });
      const point = roundedPoint(position.coords.latitude, position.coords.longitude);

      return point;
    } catch {
      // No location, no permission or no result within the limit: clocking goes on without it.
      return null;
    }
  }
}

/**
 * Returns whether NFC is switched off in the system settings (Android); an unknown state counts as on.
 *
 * @param nfc - The NFC plugin.
 * @returns `true` if the reader is off.
 */
async function isSwitchedOff(nfc: NfcPluginPort): Promise<boolean> {
  try {
    const state = await nfc.getStatus();
    const off = state.status === "NFC_DISABLED";

    return off;
  } catch {
    return false;
  }
}

/**
 * Converts a discovery of the NFC plugin into a reading.
 *
 * @param event - The discovery.
 * @returns The UID and the URI of the tag.
 */
function readingOf(event: NfcPluginEvent): NfcReading {
  const reading: NfcReading = { uid: uidHex(event.tag.id), url: ndefUri(event.tag.ndefMessage) };
  return reading;
}

/**
 * Reads tags in the foreground until the returned function is called (Android reader mode).
 *
 * @param nfc - The NFC plugin.
 * @param listener - Called with every reading.
 * @returns A function that ends the watch.
 */
function watchTags(nfc: NfcPluginPort, listener: (reading: NfcReading) => void): () => void {
  const unsubscribe = subscribePlugin(() =>
    nfc.addListener("nfcEvent", (event) => {
      listener(readingOf(event));
    }),
  );
  nfc.startScanning({ invalidateAfterFirstRead: false }).catch(() => undefined);
  const stop = (): void => {
    unsubscribe();
    nfc.stopScanning().catch(() => undefined);
  };

  return stop;
}

/**
 * Returns whether a location permission allows a position.
 *
 * @param permissions - The permissions.
 * @returns `true` if the coarse or the precise position is granted.
 */
function isGranted(permissions: LocationPermissions): boolean {
  const granted = permissions.coarseLocation === "granted" || permissions.location === "granted";
  return granted;
}

/** Status of a device whose plugin did not answer: nothing allowed, nothing registered. */
const unknownGeofenceStatus: GeofenceStatus = {
  mode: "regions",
  notifications: "denied",
  location: "denied",
  background: "denied",
  precise: false,
};

/**
 * Reads a permission state of the plugin; anything else counts as refused.
 *
 * @param value - The value of the plugin.
 * @returns The state.
 */
function permissionOf(value: unknown): GeofencePermission {
  const state: GeofencePermission = value === "granted" || value === "prompt" ? value : "denied";

  return state;
}

/**
 * Reads the status the plugin reports; a missing or unknown answer counts as nothing allowed.
 *
 * @param value - The answer of the plugin.
 * @returns The status.
 */
export function geofenceStatusOf(value: unknown): GeofenceStatus {
  if (typeof value !== "object" || value === null) {
    return unknownGeofenceStatus;
  }

  const record = value as Record<string, unknown>;
  const status: GeofenceStatus = {
    mode: record["mode"] === "shiftStart" ? "shiftStart" : "regions",
    notifications: permissionOf(record["notifications"]),
    location: permissionOf(record["location"]),
    background: permissionOf(record["background"]),
    precise: record["precise"] === true,
  };

  return status;
}

/**
 * Wraps the plugin of the geofence reminder (T-188): iOS reminds once per arming and never on
 * leaving; Android watches the regions with the background location or reminds at the shift start.
 *
 * @param plugin - The plugin.
 * @param name - The platform.
 * @returns The capability.
 */
function geofenceReminders(
  plugin: GeofenceReminderPluginPort,
  name: "ios" | "android",
): GeofenceReminders {
  const ask = async (call: () => Promise<unknown>): Promise<GeofenceStatus> => {
    try {
      const status = geofenceStatusOf(await call());
      return status;
    } catch {
      return unknownGeofenceStatus;
    }
  };
  const reminders: GeofenceReminders = {
    oneShotRegions: name === "ios",
    remindsOnExit: name === "android",
    status: () => ask(() => plugin.checkStatus()),
    requestPermissions: () => ask(() => plugin.askPermissions()),
    configure: async (setup): Promise<void> => {
      await plugin.configure(setup);
    },
    clear: async (): Promise<void> => {
      await plugin.clear();
    },
    openSettings: async (): Promise<void> => {
      await plugin.openSettings();
    },
  };

  return reminders;
}
